"use client";

import React, { useState, useEffect, useRef } from 'react';

const BackToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollPct, setScrollPct] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const onScroll = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                const scrolled = window.scrollY;
                const total = document.documentElement.scrollHeight - window.innerHeight;
                setScrollPct(total > 0 ? Math.min((scrolled / total) * 100, 100) : 0);
                setIsVisible(scrolled > 320);
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    const scrollToTop = () => {
        setIsPressed(true);
        setTimeout(() => setIsPressed(false), 200);

        // Custom eased scroll animation (easeInOutQuad) instead of native smooth scroll
        const start = window.scrollY;
        const duration = 600;
        let startTime: number | null = null;

        const easeInOutQuad = (t: number) =>
            t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            window.scrollTo(0, start * (1 - easeInOutQuad(progress)));
            if (progress < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
    };

    // SVG circle progress ring
    const size = 52;
    const strokeW = 2;
    const radius = (size - strokeW * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (scrollPct / 100) * circumference;

    return (
        <button
            onClick={scrollToTop}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label="Back to top"
            className="fixed bottom-24 right-8 z-[9999] focus:outline-none group"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible
                    ? isPressed
                        ? 'translateY(0) scale(0.92)'
                        : isHovered
                        ? 'translateY(-3px) scale(1.08)'
                        : 'translateY(0) scale(1)'
                    : 'translateY(20px) scale(0.85)',
                pointerEvents: isVisible ? 'auto' : 'none',
                transition: 'opacity 0.35s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}
        >
            {/* ── Outer glow ring (hover only) ── */}
            <span
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                    boxShadow: isHovered
                        ? '0 0 0 6px rgba(122,252,32,0.1), 0 0 28px rgba(122,252,32,0.35)'
                        : '0 0 0 0px rgba(122,252,32,0), 0 0 0px rgba(122,252,32,0)',
                    transition: 'box-shadow 0.35s ease',
                    borderRadius: '50%',
                }}
            />

            {/* ── SVG: progress ring + clip-path button face ── */}
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0"
                style={{ transform: 'rotate(-90deg)' }}
            >
                {/* Track ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="rgba(122,252,32,0.12)"
                    strokeWidth={strokeW}
                />
                {/* Progress ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#7AFC20"
                    strokeWidth={strokeW}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{
                        transition: 'stroke-dashoffset 0.2s ease',
                        filter: isHovered ? 'drop-shadow(0 0 4px #7AFC20)' : 'none',
                    }}
                />
            </svg>

            {/* ── Button face ── */}
            <span
                className="absolute flex items-center justify-center"
                style={{
                    inset: `${strokeW + 4}px`,
                    background: isHovered ? '#7AFC20' : '#081C11',
                    border: `1px solid ${isHovered ? '#7AFC20' : 'rgba(122,252,32,0.3)'}`,
                    clipPath: 'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    transition: 'background 0.25s ease, border-color 0.25s ease',
                }}
            >
                {/* Arrow icon — animates up on hover */}
                <svg
                    className="relative z-10"
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke={isHovered ? '#081C11' : '#7AFC20'}
                    strokeWidth={3}
                    style={{
                        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                        transition: 'transform 0.25s ease, stroke 0.25s ease',
                    }}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                </svg>
            </span>

            {/* ── Tooltip ── */}
            <span
                className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1.5 pointer-events-none"
                style={{
                    background: '#081C11',
                    border: '1px solid rgba(122,252,32,0.2)',
                    color: '#7AFC20',
                    clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))',
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered
                        ? 'translateY(-50%) translateX(0)'
                        : 'translateY(-50%) translateX(6px)',
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                }}
            >
                Back to top
            </span>
        </button>
    );
};

export default BackToTopButton;
